import { pctTexto, semDiferenca } from '@via-tax/contracts'
import { Aviso, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { reais } from '../formatos'
import type { CasoConcretoContraparte } from '../tipos'

/**
 * A conversa de preço com este cliente, nos números reais do contrato dele: quanto a nota sobe, quanto dela
 * volta como crédito e como fica o custo real dele. É a pergunta que o cliente empresa faz primeiro, porque
 * ele decide pelo CUSTO — e ninguém repassa a alíquota cheia sobre o valor de hoje.
 *
 * Os três pontos vêm prontos da API (`montarRepasse` em contracts, sobre a `zona` daquele contrato): o piso
 * mantém a margem da empresa, o teto deixa o custo do cliente igual ao de hoje, e o meio divide o ganho.
 */

type Repasse = NonNullable<CasoConcretoContraparte['repasse']>
const POSICOES = [
  ['piso', 'Piso', 'mantém a sua margem'],
  ['sugerido', 'Meio', 'divide o ganho'],
  ['teto', 'Teto', 'o cliente paga o mesmo'],
] as const

const comSinal = (ppm: number) => `${ppm < 0 ? '−' : '+'}${pctTexto(ppm)}`

export function RepasseContraparte({ r, ano, meses }: { r: Repasse; ano: number; meses: number }) {
  const [posicao, setPosicao] = useState<'piso' | 'sugerido' | 'teto'>('piso')
  const [copiado, setCopiado] = useState(false)
  const p = r[posicao]
  const custoIgual = semDiferenca(p.variacaoCusto)
  const corCusto = custoIgual ? '' : p.variacaoCusto < 0 ? 'text-ok' : 'text-alerta'

  const copiar = () => {
    navigator.clipboard?.writeText(p.frase).then(
      () => {
        setCopiado(true)
        setTimeout(() => setCopiado(false), 2500)
      },
      () => setCopiado(false),
    )
  }

  return (
    <section aria-labelledby="repasse-titulo" className="flex flex-col gap-4 rounded-md border border-linha bg-fundo p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="repasse-titulo" className="text-sm font-semibold">
            O que dizer a este cliente
          </h3>
          <p className="mt-0.5 text-[11px] text-tinta-3">
            Sobre as notas deste contrato nos {meses} {meses === 1 ? 'mês' : 'meses'} do período, com as alíquotas de {ano} ({pctTexto(r.aliquotaMedia)} em média)
          </p>
        </div>
        <div className="flex gap-1.5">
          {POSICOES.map(([chave, rotulo, nota]) => (
            <button
              key={chave}
              type="button"
              onClick={() => setPosicao(chave)}
              aria-pressed={posicao === chave}
              title={nota}
              className={`rounded-md border px-2.5 py-1 text-[11px] font-medium ${posicao === chave ? 'border-navy bg-navy text-white' : 'border-linha text-navy hover:border-navy'}`}
            >
              {rotulo}
            </button>
          ))}
        </div>
      </div>

      {!r.existe && (
        <Aviso>
          Neste contrato não há preço em que os dois lados ganhem: o menor preço que mantém a sua margem já custa ao cliente mais do que ele pagaria hoje. Os números abaixo mostram o
          tamanho da diferença.
        </Aviso>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-md bg-painel p-3">
          <p className="text-[11px] text-tinta-3">O valor do contrato sobe</p>
          <p className="num text-xl font-semibold">{comSinal(p.aumentoNota)}</p>
          <p className="text-[11px] text-tinta-3">
            de {reais(p.linhas[0].nota)} para {reais(p.linhas[2].nota)}
          </p>
        </div>
        <div className="rounded-md bg-painel p-3">
          <p className="text-[11px] text-tinta-3">Volta para ele como crédito</p>
          <p className="num text-xl font-semibold text-ok">{pctTexto(p.creditoSobreNota)}</p>
          <p className="text-[11px] text-tinta-3">{reais(p.linhas[2].credito)} da nota nova</p>
        </div>
        <div className="rounded-md bg-navy p-3 text-white">
          <p className="text-[11px] text-white/75">O custo real dele</p>
          <p className="num text-xl font-semibold">{custoIgual ? 'o mesmo' : comSinal(p.variacaoCusto)}</p>
          <p className="text-[11px] text-white/75">
            {reais(p.linhas[0].custo)} → {reais(p.linhas[2].custo)}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-md bg-painel p-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[13px]">{p.frase}</p>
          <button type="button" onClick={copiar} className="shrink-0 rounded-md border border-linha px-2 py-1 text-[11px] font-medium text-navy hover:border-navy">
            {copiado ? 'copiado' : 'copiar'}
          </button>
        </div>
        <p className="text-[11px] text-tinta-3">{p.resumo}</p>
      </div>

      <p className="text-[11px] text-tinta-3 sm:hidden">Arraste a tabela para o lado para ver todas as colunas.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[34rem] text-[13px]">
          <thead>
            <tr className="border-b border-linha text-left text-[11px] text-tinta-3">
              <th className="py-2 font-medium" />
              <th className="py-2 pl-4 text-right font-medium">Valor da nota</th>
              <th className="py-2 pl-4 text-right font-medium">Crédito dele</th>
              <th className="py-2 pl-4 text-right font-medium">Custo real dele</th>
              <th className="py-2 pl-4 text-right font-medium">Fica para a empresa</th>
            </tr>
          </thead>
          <tbody>
            {p.linhas.map((l, i) => (
              <tr key={l.rotulo} className={`border-b border-linha ${i === 2 ? 'font-medium' : ''}`}>
                <td className="py-1.5">{l.rotulo}</td>
                <td className="num whitespace-nowrap py-1.5 pl-4 text-right">{reais(l.nota)}</td>
                <td className="num whitespace-nowrap py-1.5 pl-4 text-right text-ok">{l.credito ? reais(l.credito) : '—'}</td>
                <td className={`num whitespace-nowrap py-1.5 pl-4 text-right ${i === 2 ? corCusto : ''}`}>{reais(l.custo)}</td>
                <td className="num whitespace-nowrap py-1.5 pl-4 text-right">{reais(l.sobra)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="list-disc space-y-1 pl-5 text-[11px] text-tinta-3">
        <li>
          {posicao === 'piso'
            ? `Este é o piso: a sua margem fica a mesma de hoje (diferença de ${reais(Math.abs(p.margem))} no período). Acima dele o ganho começa a ficar com a empresa.`
            : `Neste preço fica para a empresa ${reais(Math.abs(p.margem))} ${p.margem >= 0 ? 'a mais' : 'a menos'} no período do que ficaria continuando como está.`}
        </li>
        <li>
          O crédito das suas compras <strong>não entra</strong> nesta conta{r.creditoCompras ? ` (${reais(r.creditoCompras)} ficaram de fora)` : ''}: ele é ganho da empresa, não
          deste contrato — contá-lo aqui baixaria o preço de um cliente com a compra feita de outro.
        </li>
        <li>
          Custo de hoje{' '}
          {r.base === 'presumido'
            ? 'pelo Lucro Presumido: ele não aproveita nada do imposto que já vem embutido na nota'
            : 'pelo Lucro Real: já descontado o PIS/COFINS que ele credita hoje'}
          .{' '}
          {p.linhas[1].custo !== p.linhas[0].custo &&
            `Se a empresa continuar como está, em ${ano} ele passa a aproveitar ${reais(p.linhas[1].credito)} (custo de ${reais(p.linhas[1].custo)}) — é contra isso que ele vai comparar.`}
        </li>
      </ul>
      <p className="flex flex-wrap items-center gap-2 text-[11px] text-tinta-3">
        <Selo tom="neutro">atenção</Selo> A alíquota de {ano} ainda é estimativa; a conta se refaz quando o número oficial sair.
      </p>
    </section>
  )
}
